// React
import { useRef } from 'react';

// Types
interface VideoControlsProps {
  isPlaying: boolean;
  isMuted: boolean;
  currentTime: number;
  duration: number;
  onPlayPause: () => void;
  onMuteToggle: () => void;
  onSeek: (time: number) => void;
}

// Icons - Pixel-perfect from Figma
const PauseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Two thin vertical bars matching Figma design */}
    <rect x="4" y="2.67" width="2" height="10.67" rx="0.5" fill="currentColor" />
    <rect x="10" y="2.67" width="2" height="10.67" rx="0.5" fill="currentColor" />
  </svg>
);

const PlayIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Triangle play icon matching Figma design */}
    <path
      d="M5.33 3.2V12.8C5.33 13.2 5.8 13.47 6.13 13.2L12.8 8.4C13.07 8.2 13.07 7.8 12.8 7.6L6.13 2.8C5.8 2.53 5.33 2.8 5.33 3.2Z"
      fill="currentColor"
    />
  </svg>
);

const VolumeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Speaker base */}
    <path
      d="M7.33 3.29V12.71C7.33 13.18 6.78 13.42 6.44 13.1L3.71 10.67H2C1.45 10.67 1 10.22 1 9.67V6.33C1 5.78 1.45 5.33 2 5.33H3.71L6.44 2.9C6.78 2.58 7.33 2.82 7.33 3.29Z"
      fill="currentColor"
    />
    {/* Sound waves */}
    <path
      d="M10 5.5C10.8 6.3 10.8 9.7 10 10.5"
      stroke="currentColor"
      strokeWidth="1.33"
      strokeLinecap="round"
    />
    <path
      d="M12 3.67C13.5 5.17 13.5 10.83 12 12.33"
      stroke="currentColor"
      strokeWidth="1.33"
      strokeLinecap="round"
    />
  </svg>
);

const VolumeMutedIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Speaker base */}
    <path
      d="M7.33 3.29V12.71C7.33 13.18 6.78 13.42 6.44 13.1L3.71 10.67H2C1.45 10.67 1 10.22 1 9.67V6.33C1 5.78 1.45 5.33 2 5.33H3.71L6.44 2.9C6.78 2.58 7.33 2.82 7.33 3.29Z"
      fill="currentColor"
    />
    {/* Mute X */}
    <path
      d="M14 5.5L10 10.5M10 5.5L14 10.5"
      stroke="currentColor"
      strokeWidth="1.33"
      strokeLinecap="round"
    />
  </svg>
);

export const VideoControls = ({
  isPlaying,
  isMuted,
  currentTime,
  duration,
  onPlayPause,
  onMuteToggle,
  onSeek,
}: VideoControlsProps) => {
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Calculate progress percentage
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Handle click on progress bar for seeking
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current || duration === 0) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const clickPosition = e.clientX - rect.left;
    const percentage = clickPosition / rect.width;
    const newTime = percentage * duration;

    onSeek(Math.max(0, Math.min(newTime, duration)));
  };

  return (
    <div className="flex items-center gap-[16px] w-full h-[67px] px-[28px] bg-gray-100 border-t-2 border-gray-200">
      {/* Left control: Play/Pause toggle */}
      <div className="flex items-center shrink-0">
        <button
          type="button"
          onClick={onPlayPause}
          className="flex items-center justify-center w-[16px] h-[16px] text-gray-800 hover:text-gray-1000 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-800 focus-visible:ring-offset-2 rounded"
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </button>
      </div>

      {/* Progress bar */}
      <div
        ref={progressBarRef}
        onClick={handleProgressClick}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') {
            onSeek(Math.min(currentTime + 5, duration));
          } else if (e.key === 'ArrowLeft') {
            onSeek(Math.max(currentTime - 5, 0));
          }
        }}
        className="relative flex-1 h-[8px] bg-gray-600 rounded-[27px] cursor-pointer"
        role="slider"
        aria-label="Video progress"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        tabIndex={0}
      >
        <div
          className="absolute top-0 left-0 h-full bg-gray-800 rounded-[32px] transition-[width] duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Right control: Volume */}
      <div className="flex items-center shrink-0">
        <button
          type="button"
          onClick={onMuteToggle}
          className="flex items-center justify-center w-[16px] h-[16px] text-gray-800 hover:text-gray-1000 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-800 focus-visible:ring-offset-2 rounded"
          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
        >
          {isMuted ? <VolumeMutedIcon /> : <VolumeIcon />}
        </button>
      </div>
    </div>
  );
};
